/**
 * Data layer for `igaming-scheduled-bi-warehouse-exports-etl`.
 * iGaming warehouse ETL export configs: BigQuery / Snowflake / Redshift.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type WarehouseType = "bigquery" | "snowflake" | "redshift";
export type PipelineStatus =
	| "queued"
	| "running"
	| "ready"
	| "error"
	| "idle";

export interface IgamingScheduledBiWarehouseExportsEtlRow {
	id: string;
	tenant_id: string;
	destination: string;
	warehouse_type: WarehouseType;
	schedule: string;
	dataset: string;
	table_prefix?: string | null;
	retention_policy?: string | null;
	last_run_at?: number | null;
	status: PipelineStatus;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingScheduledBiWarehouseExportsEtlResponse {
	count: number;
	items: IgamingScheduledBiWarehouseExportsEtlRow[];
}

export interface CreateIgamingScheduledBiWarehouseExportsEtlBody {
	destination: string;
	warehouse_type: WarehouseType;
	schedule: string;
	dataset: string;
	table_prefix?: string;
	retention_policy?: string;
	credentials?: Record<string, string>;
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-scheduled-bi-warehouse-exports-etl";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingScheduledBiWarehouseExportsEtlResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingScheduledBiWarehouseExportsEtlResponse>(res);
}

export async function create(
	body: CreateIgamingScheduledBiWarehouseExportsEtlBody,
): Promise<IgamingScheduledBiWarehouseExportsEtlRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingScheduledBiWarehouseExportsEtlRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingScheduledBiWarehouseExportsEtlQueryKeys = {
	all: ["igaming-scheduled-bi-warehouse-exports-etl"] as const,
	list: () => [...igamingScheduledBiWarehouseExportsEtlQueryKeys.all] as const,
	detail: (id: string) =>
		[...igamingScheduledBiWarehouseExportsEtlQueryKeys.all, id] as const,
};
